import type {
  PosSalesLayoutCardAspectRatio,
  PosSalesLayoutDocument,
  PosSalesLayoutRuntimePayload,
} from "../gateway/sales-layout-client";
import { indexedScope } from "./indexed-runtime";
import { posRuntimeDb } from "./pos-db";
import type { TrustedTerminalIdentity } from "./terminal-state";

const DOMAIN = "SALES_LAYOUT";

function scopeKey(identity: TrustedTerminalIdentity) {
  return indexedScope(identity, "sales-layout");
}

function projectionKey(identity: TrustedTerminalIdentity) {
  return `${scopeKey(identity)}:effective`;
}

function safeRevision(value: unknown) {
  const revision = Number(value);
  return Number.isSafeInteger(revision) && revision >= 0 ? revision : 0;
}

export async function readLocalSalesLayoutRevision(identity: TrustedTerminalIdentity) {
  const row = await posRuntimeDb.projections.get(projectionKey(identity));
  return safeRevision(row?.revision);
}

export async function applySalesLayoutRuntime(
  identity: TrustedTerminalIdentity,
  payload: PosSalesLayoutRuntimePayload,
) {
  const key = projectionKey(identity);
  const scoped = scopeKey(identity);
  const latestRevision = safeRevision(payload.latest_revision);
  const eligibleEvents = [...(payload.events ?? [])]
    .filter((event) => safeRevision(event.revision) <= latestRevision)
    .sort((left, right) => safeRevision(left.revision) - safeRevision(right.revision));
  const latestEvent = eligibleEvents.length > 0
    ? eligibleEvents[eligibleEvents.length - 1]
    : undefined;

  await posRuntimeDb.transaction("rw", posRuntimeDb.projections, async () => {
    if (!latestEvent) {
      if (payload.mode === "FULL") {
        await posRuntimeDb.projections.put({
          key,
          scopeKey: scoped,
          domain: DOMAIN,
          revision: String(latestRevision),
          payload: null,
          updatedAt: new Date().toISOString(),
        });
      }
      return;
    }

    const eventRevision = safeRevision(latestEvent.revision);
    const document = latestEvent.runtime_action === "UPSERT" && latestEvent.layout_document
      ? latestEvent.layout_document
      : null;

    await posRuntimeDb.projections.put({
      key,
      scopeKey: scoped,
      domain: DOMAIN,
      revision: String(eventRevision),
      payload: document,
      updatedAt: new Date().toISOString(),
    });
  });
}

export async function readLocalSalesLayoutDocument(
  identity: TrustedTerminalIdentity,
): Promise<PosSalesLayoutDocument | null> {
  const row = await posRuntimeDb.projections.get(projectionKey(identity));
  if (!row?.payload || typeof row.payload !== "object" || Array.isArray(row.payload)) return null;
  return row.payload as PosSalesLayoutDocument;
}

function positiveInteger(value: unknown): value is number {
  return Number.isSafeInteger(value) && Number(value) > 0;
}

export async function readLocalSalesLayoutCardAspectRatio(
  identity: TrustedTerminalIdentity,
): Promise<PosSalesLayoutCardAspectRatio | null> {
  const document = await readLocalSalesLayoutDocument(identity);
  const ratio = document?.card_presentation?.aspect_ratio;
  if (!ratio || !positiveInteger(ratio.width) || !positiveInteger(ratio.height)) return null;
  return { width: ratio.width, height: ratio.height };
}

export async function purgeSalesLayoutRuntimeOutsideScope(identity: TrustedTerminalIdentity) {
  const keepScope = scopeKey(identity);
  await posRuntimeDb.projections
    .where("domain")
    .equals(DOMAIN)
    .and((row) => row.scopeKey !== keepScope)
    .delete();
}
